import pluralize from "pluralize";
import React, { useState } from "react";
import { useWebOnboardingSteps } from "../../hooks/useWebOnboardingSteps";
import { ButtonGroup, StepCounter, StepLayout } from "../../shared-components";
import { InviteSlide } from "./InviteSlide";

import { useFeatureFlag } from "#webapp/src/launch-darkly/useLaunchDarkly";
import { useGatewayMutation } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { map, uniqBy } from "lodash";
import { TrackingEvents, track } from "../../../../util/analytics.util";
import { InviteSearchPerson } from "../../../invite-search";
import { BulkInviteEvent } from "../../../invite-search/types";
import { setError } from "../../utils/setError";
import { useInviteStepContext } from "./InviteContext";
import { SendInvitesDocument } from "./__generated__/InviteStep.v2.generated";

export const InviteStep = () => {
  const [enableSelectAll] = useFeatureFlag("OnboardingInviteSelectAll");
  const { goForward, goBack, shouldShowSecondaryButton } = useWebOnboardingSteps();
  const { invitees, addInvitees, removeInvitees } = useInviteStepContext();

  const [sendInvites] = useGatewayMutation(SendInvitesDocument);

  // Track which bulk invite events were selected for analytics purposes
  const [selectedBulkInvites, setSelectedBulkInvites] = useState<BulkInviteEvent[]>([]); // used only for tracking purposes
  const [disableButtons, setDisableButtons] = useState(false);

  const updateBulkInviteEvents = (checked: boolean, bulkInviteEvent: BulkInviteEvent) => {
    if (checked) {
      setSelectedBulkInvites(uniqBy([...selectedBulkInvites, bulkInviteEvent], "title"));
    } else {
      setSelectedBulkInvites(
        selectedBulkInvites.filter(({ title }) => title !== bulkInviteEvent.title),
      );
    }
  };

  const updateSelectedUsers = (
    selectedInvitees: InviteSearchPerson[],
    checked: boolean,
    bulkInviteEvent?: BulkInviteEvent,
  ) => {
    if (checked) {
      addInvitees(selectedInvitees);
    } else {
      removeInvitees(selectedInvitees);
    }

    if (bulkInviteEvent) {
      updateBulkInviteEvents(checked, bulkInviteEvent);
    }
  };

  const onSaveAndGoForward = async () => {
    if (invitees.length > 0) {
      const calendarIds = map(invitees, "primaryCalendarId");
      const trackedProperties = {
        inviteCount: invitees.length,
        invitedCalendarIds: calendarIds,
        bulkInvites: selectedBulkInvites,
        numSelectedBulkInvites: selectedBulkInvites.length,
        featureFlags: { OnboardingInviteSelectAll: enableSelectAll },
      };
      track(TrackingEvents.WEB_ONBOARDING.INVITE_SENT, trackedProperties);
      setDisableButtons(true);

      try {
        await sendInvites({ variables: { calendarIds } });
        track(TrackingEvents.WEB_ONBOARDING.INVITE_SENT_ON_COMPLETE, trackedProperties);
      } catch (error) {
        setError({ error, message: "failed to send invites", showUserMessage: true });
      } finally {
        // Clear selected invitee state
        removeInvitees(invitees);
        setDisableButtons(false);
        goForward();
      }
    } else {
      track(TrackingEvents.WEB_ONBOARDING.INVITE_SKIPPED, {
        featureFlags: { OnboardingInviteSelectAll: enableSelectAll },
      });
      goForward();
    }
  };

  const primaryButtonText =
    invitees.length > 0 ? `Invite ${pluralize("person", invitees.length, true)}` : "Continue";

  return (
    <StepLayout>
      <StepCounter />
      <InviteSlide selectedInvitees={invitees} onCheck={updateSelectedUsers} />
      <ButtonGroup
        shouldShowSecondary={shouldShowSecondaryButton}
        primaryLabel={primaryButtonText}
        onClickPrimary={onSaveAndGoForward}
        onClickSecondary={goBack}
        disabled={disableButtons}
      />
    </StepLayout>
  );
};
